import { useState, useCallback, useEffect, useRef } from 'react'
import { Message, Session, Department } from '../types/communication.types'
import { CommunicationAPI } from '../services/communicationApi'
import { messageEventBus, MessageEvent } from '../utils/messageEventBus'
import { getBrazilTimestamp } from '../../../../utils/formater'

interface UseMessagesOptions {
  session: Session | null
  onNewMessage?: (message: Message) => void
  autoMarkAsRead?: boolean
  // Dados do agente para identificação
  agentData?: {
    agentId: string
    agentEmail: string
    agentName: string
    hotelId: number
  }
  // 🎯 Canal para envio de mensagens (sobrescreve session.department)
  channelOverride?: Department
}

interface UseMessagesReturn {
  // Estado das mensagens
  messages: Message[]
  isLoadingMessages: boolean
  messagesError: string | null
  hasMoreMessages: boolean
  
  // Funções de mensagens
  loadMessages: () => Promise<void>
  loadMoreMessages: () => Promise<void>
  sendMessage: (text: string, imageUrl?: string) => Promise<void>
  uploadImage: (file: File) => Promise<string>
  markAsRead: () => Promise<void>
  refreshMessages: () => Promise<void>
  addNewMessage: (message: Message) => void
  
  // Typing indicator
  isTyping: boolean
  setIsTyping: (typing: boolean) => void
  
  // Estado de envio
  isSending: boolean
  isUploading: boolean
}

export const useMessages = (options: UseMessagesOptions): UseMessagesReturn => {
  const { session, onNewMessage, autoMarkAsRead = true, agentData, channelOverride } = options
  
  // Estados
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [messagesError, setMessagesError] = useState<string | null>(null)
  const [hasMoreMessages, setHasMoreMessages] = useState(true)
  const [isTyping, setIsTyping] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  
  // Refs para paginação
  const offsetRef = useRef(0)
  const limitRef = useRef(50)
  const isLoadingRef = useRef(false)
  
  // Marcar sessão como lida
  const markAsRead = useCallback(async () => {
    if (!session) return
    
    try {
      console.log(`👁️ Marcando sessão ${session.sessionKey} como lida`)
      
      await CommunicationAPI.markSessionAsRead(session.hotelId, session.sessionKey)
      
      console.log('✅ Sessão marcada como lida')
      
    } catch (error) {
      console.error('❌ Erro ao marcar como lida:', error)
    }
  }, [session])

  // 🚀 CONECTAR AO EVENT BUS PARA RECEBER MENSAGENS EM TEMPO REAL
  useEffect(() => {
    const handleRealtimeMessage = (event: MessageEvent) => {
      console.log('📨 [useMessages] Evento recebido do event bus:', {
        eventSessionKey: event.sessionKey,
        currentSessionKey: session?.sessionKey,
        messageId: event.message.id,
        messageAuthor: event.message.author,
        messageText: event.message.text?.substring(0, 50),
        willProcess: event.sessionKey === session?.sessionKey
      })
      
      // Só processar se for da sessão ativa
      if (!session || event.sessionKey !== session.sessionKey) {
        console.log('⏭️ [useMessages] Ignorando mensagem - sessão diferente ou null')
        return
      }
      
      console.log('✅ [useMessages] Processando mensagem em tempo real:', {
        messageId: event.message.id,
        author: event.message.author,
        text: event.message.text?.substring(0, 50)
      })
      
      // Adicionar mensagem ao estado
      setMessages(prev => {
        // Evitar duplicatas
        const exists = prev.some(msg => msg.id === event.message.id)
        if (exists) {
          console.log('⚠️ [useMessages] Mensagem já existe, ignorando duplicata:', event.message.id)
          return prev
        }
        
        const newMessages = [...prev, event.message]
        console.log(`📬 [useMessages] Mensagem adicionada! Total: ${newMessages.length}`)
        console.log('🎯 [VALIDAÇÃO] Mensagem exibida automaticamente:', {
          autor: event.message.author,
          texto: event.message.text?.substring(0, 50),
          sessão: event.guestName,
          timestamp: new Date().toISOString()
        })
        return newMessages
      })
      
      // Callback para componente pai
      onNewMessage?.(event.message)
      
      // Auto-marcar como lida se for mensagem do hóspede
      if (autoMarkAsRead && event.message.author === 'guest') {
        setTimeout(() => markAsRead(), 1000)
      }
    }

    // Registrar no event bus
    const unsubscribe = messageEventBus.subscribe(handleRealtimeMessage)
    
    console.log(`📡 [useMessages] Registrado no event bus para sessão ${session?.sessionKey || 'nenhuma'}`)
    console.log('🎯 [VALIDAÇÃO] useMessages pronto para receber mensagens em tempo real')
    
    // Cleanup
    return () => {
      unsubscribe()
      console.log(`📡 [useMessages] Removido do event bus para sessão ${session?.sessionKey || 'nenhuma'}`)
    }
  }, [session, onNewMessage, autoMarkAsRead, markAsRead])
  
  // 🎯 LISTENER PARA MENSAGENS ENVIADAS VIA SUGESTÃO DA IA
  useEffect(() => {
    const handleNewMessageSent = (event: CustomEvent) => {
      const { sessionKey, message } = event.detail
      
      console.log('📤 [useMessages] Mensagem enviada via IA recebida:', {
        sessionKey,
        currentSessionKey: session?.sessionKey,
        messageText: message.text?.substring(0, 50)
      })
      
      // Só processar se for da sessão ativa
      if (!session || sessionKey !== session.sessionKey) {
        console.log('⏭️ [useMessages] Ignorando - sessão diferente')
        return
      }
      
      // Adicionar mensagem ao estado IMEDIATAMENTE
      setMessages(prev => {
        // Evitar duplicatas
        const exists = prev.some(msg => msg.id === message.id)
        if (exists) {
          console.log('⚠️ [useMessages] Mensagem já existe, ignorando')
          return prev
        }
        
        const newMessages = [...prev, message]
        console.log(`✅ [useMessages] Mensagem da IA adicionada ao histórico! Total: ${newMessages.length}`)
        return newMessages
      })
    }
    
    window.addEventListener('new-message-sent', handleNewMessageSent as EventListener)
    
    return () => {
      window.removeEventListener('new-message-sent', handleNewMessageSent as EventListener)
    }
  }, [session])
  
  // Carregar mensagens iniciais (histórico completo do banco ChatMessage)
  const loadMessages = useCallback(async () => {
    if (!session || isLoadingRef.current) return
    
    setIsLoadingMessages(true)
    setMessagesError(null)
    isLoadingRef.current = true
    
    console.log(`🔄 Carregando histórico completo da sessão: ${session.sessionKey}`)
    
    try {
      // 🔄 ESTRATÉGIA DE MÚLTIPLAS TENTATIVAS PARA HISTÓRICO AGREGADO
      let messages: any[] = []
      let loadedSuccessfully = false
      
      // Prioridade 1: Tentar buscar por reservationId (histórico completo agregado)
      if (session.reservationId && !loadedSuccessfully) {
        try {
          console.log(`🔄 Tentando carregar histórico agregado da reserva ${session.reservationId}`)
          messages = await CommunicationAPI.getReservationMessages(session.hotelId, session.reservationId)
          console.log(`✅ Histórico agregado da reserva carregado: ${messages.length} mensagens`)
          loadedSuccessfully = true
        } catch (reservationError) {
          console.warn('⚠️ Histórico agregado por reserva não disponível:', reservationError)
        }
      }
      
      // Prioridade 2: Tentar buscar por guestName (histórico do hóspede)
      if (session.guestName && !loadedSuccessfully) {
        try {
          console.log(`🔄 Tentando carregar histórico do hóspede ${session.guestName}`)
          messages = await CommunicationAPI.getGuestMessages(session.hotelId, session.guestName)
          console.log(`✅ Histórico do hóspede carregado: ${messages.length} mensagens`)
          loadedSuccessfully = true
        } catch (guestError) {
          console.warn('⚠️ Histórico por hóspede não disponível:', guestError)
        }
      }
      
      // Prioridade 3: Fallback para sessão única (comportamento atual)
      if (!loadedSuccessfully) {
        try {
          console.log(`🔄 Carregando apenas da sessão atual: ${session.sessionKey}`)
          messages = await CommunicationAPI.getMessages(session.hotelId, session.sessionKey)
          console.log(`✅ Histórico da sessão única carregado: ${messages.length} mensagens`)
          loadedSuccessfully = true
        } catch (sessionError) {
          console.warn('⚠️ Erro ao carregar histórico da sessão:', sessionError)
          throw sessionError // Vai para o catch principal
        }
      }
      
      if (loadedSuccessfully && messages) {
        // Ordenar por data para manter cronologia
        messages.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime())
        
        setMessages(messages)
        setHasMoreMessages(false) // Histórico completo já carregado
        
      } else {
        throw new Error('Nenhuma mensagem encontrada em todos os métodos')
      }
      
    } catch (allHistoryErrors) {
      console.warn('⚠️ Todos os métodos de histórico falharam, usando API paginada:', allHistoryErrors)
      
      try {
        // Fallback final para API paginada
        const response = await CommunicationAPI.getMessagesPaginated(
          session.hotelId,
          session.sessionKey,
          {
            limit: limitRef.current,
            offset: 0
          }
        )
        
        console.log(`✅ ${response.messages.length} mensagens carregadas (fallback paginado)`)
        
        setMessages(response.messages.reverse()) // Reverter para ordem cronológica
        setHasMoreMessages(response.hasMore)
        offsetRef.current = response.messages.length
        
      } catch (fallbackError) {
        console.error('❌ Erro crítico: Todos os métodos de carregamento falharam:', fallbackError)
        setMessagesError('Erro ao carregar mensagens. Tente novamente.')
      }
    }
    
    // Marcar como lida automaticamente se habilitado
    if (autoMarkAsRead) {
      await markAsRead()
    }
    
    setIsLoadingMessages(false)
    isLoadingRef.current = false
  }, [session, autoMarkAsRead, markAsRead])
  
  // Carregar mais mensagens (paginação)
  const loadMoreMessages = useCallback(async () => {
    if (!session || !hasMoreMessages || isLoadingRef.current) return
    
    try {
      isLoadingRef.current = true
      
      console.log(`🔄 Carregando mais mensagens (offset: ${offsetRef.current})`)
      
      const response = await CommunicationAPI.getMessagesPaginated(
        session.hotelId,
        session.sessionKey,
        {
          limit: limitRef.current,
          offset: offsetRef.current
        }
      )
      
      console.log(`✅ ${response.messages.length} mensagens adicionais carregadas`)
      
      // Adicionar mensagens no início (são mais antigas)
      setMessages(prev => [...response.messages.reverse(), ...prev])
      setHasMoreMessages(response.hasMore)
      offsetRef.current += response.messages.length
      
    } catch (error) {
      console.error('❌ Erro ao carregar mais mensagens:', error)
    } finally {
      isLoadingRef.current = false
    }
  }, [session, hasMoreMessages])
  
  // Enviar mensagem
  const sendMessage = useCallback(async (text: string, imageUrl?: string) => {
    if (!session || !text.trim() || isSending) {
      console.log('❌ Não foi possível enviar mensagem:', {
        hasSession: !!session,
        hasText: !!text.trim(),
        isSending
      })
      return
    }
    
    const tempId = Date.now() // ID único para mensagem temporária
    
    // 🎯 USAR CHANNEL OVERRIDE se fornecido, senão usar department da sessão
    const channelToUse = channelOverride || session.department || 'recepcao'
    
    try {
      setIsSending(true)
      
      console.log(`📤 Enviando mensagem para sessão ${session.sessionKey}:`, {
        text: text.trim(),
        imageUrl,
        hotelId: session.hotelId,
        sessionKey: session.sessionKey,
        channelToUse,
        channelOverride,
        sessionDepartment: session.department
      })
      
      // Criar mensagem temporária para feedback imediato
      const tempMessage: Message = {
        id: tempId,
        sessionId: session.id,
        author: 'agent',
        text: text.trim(),
        imageUrl,
        lang: session.lang,
        channel: channelToUse,
        deliveryStatus: 'sent',
        created: getBrazilTimestamp(),
        sentAt: getBrazilTimestamp()
      }
      
      // Adicionar mensagem temporariamente
      setMessages(prev => [...prev, tempMessage])
      
      // Enviar via API (nova versão que salva no banco ChatMessage)
      console.log('🔄 Dados da sessão para envio:', {
        sessionId: session.id,
        sessionIdType: typeof session.id,
        sessionIdUndefined: session.id === undefined,
        hotelId: session.hotelId,
        sessionKey: session.sessionKey,
        channelToUse,
        channelOverride,
        sessionDepartment: session.department
      })
      
      // 🚨 VALIDAÇÃO CRÍTICA: Verificar se sessionId é válido
      if (!session.id || session.id === undefined) {
        console.error('❌ ERRO CRÍTICO: session.id é undefined!', {
          session,
          sessionKey: session.sessionKey,
          department: session.department
        })
        throw new Error('Session ID inválido - não é possível enviar mensagem')
      }
      
      // � WORKAROUND CRÍTICO: Backend /bidirectional tem bug que ignora o channel
      // Quando há filtro de departamento (channelOverride), pular /bidirectional e ir direto para /admin-message
      const forcarFallback = !!channelOverride
      
      // �🔍 LOG IMEDIATAMENTE ANTES DO ENVIO
      console.log('📤 ENVIANDO MENSAGEM COM PARÂMETROS:', {
        sessionId: session.id,
        channelToUse,
        channelOverride,
        forcarFallback,
        rota: (agentData && !forcarFallback) ? 'BIDIRECTIONAL (/bidirectional)' : 'FALLBACK (/admin-message)',
        razao: forcarFallback ? '🚨 Forçando fallback para evitar bug de channel no backend' : 'Normal',
        agentId: agentData?.agentId,
        conversationId: agentData ? `conv-${session.id}` : undefined,
        targetSessionId: agentData ? session.id : undefined
      })
      
      // Usar nova API bidirecional SOMENTE se:
      // 1. Temos dados do agente (agentData)
      // 2. NÃO há filtro de departamento (para evitar bug do backend)
      const sentMessage = (agentData && !forcarFallback)
        ? await CommunicationAPI.sendBidirectionalMessage(
            session.id, // sessionId para salvar no banco
            text.trim(),
            imageUrl,
            channelToUse,
            agentData
          )
        : await CommunicationAPI.sendAdminMessage(
            session.id, // sessionId para salvar no banco
            text.trim(),
            imageUrl,
            channelToUse,
            agentData // 🔥 PASSAR agentData também para o fallback!
          )
      
      // Substituir mensagem temporária pela real
      setMessages(prev => 
        prev.map(msg => 
          msg.id === tempId ? sentMessage : msg
        )
      )
      
      console.log('✅ Mensagem enviada com sucesso:', sentMessage)
      
    } catch (error) {
      console.error('❌ Erro ao enviar mensagem:', error)
      
      // Remover mensagem temporária em caso de erro
      setMessages(prev => prev.filter(msg => msg.id !== tempId))
      
      // Re-throw para o componente tratar
      throw error
    } finally {
      setIsSending(false)
    }
  }, [session, isSending, agentData, channelOverride])

  // Upload de imagem
  const uploadImage = useCallback(async (file: File): Promise<string> => {
    if (!session) {
      throw new Error('Sessão não disponível para upload')
    }

    try {
      setIsUploading(true)
      console.log(`📷 Fazendo upload de imagem para sessão ${session.sessionKey}`)
      
      const response = await CommunicationAPI.uploadChatImage(file)
      const imageUrl = response.url || response.imageUrl
      
      console.log('✅ Upload de imagem concluído:', imageUrl)
      return imageUrl
      
    } catch (error) {
      console.error('❌ Erro ao fazer upload de imagem:', error)
      throw error
    } finally {
      setIsUploading(false)
    }
  }, [session])
  
  // Atualizar mensagens
  const refreshMessages = useCallback(async () => {
    offsetRef.current = 0
    await loadMessages()
  }, [loadMessages])
  
  // Adicionar nova mensagem (chamada externamente via WebSocket)
  const addNewMessage = useCallback((message: Message) => {
    console.log('📨 Nova mensagem recebida:', message)
    
    setMessages(prev => {
      // Evitar duplicatas
      const exists = prev.some(msg => msg.id === message.id)
      if (exists) return prev
      
      return [...prev, message]
    })
    
    // Callback para componente pai
    onNewMessage?.(message)
    
    // Auto-marcar como lida se estiver configurado
    if (autoMarkAsRead && message.author === 'guest') {
      setTimeout(markAsRead, 1000) // Delay para evitar spam
    }
  }, [onNewMessage, autoMarkAsRead, markAsRead])
  
  // Limpar mensagens quando a sessão muda
  useEffect(() => {
    if (!session) {
      setMessages([])
      setMessagesError(null)
      setHasMoreMessages(true)
      offsetRef.current = 0
      return
    }
    
    // Carregar mensagens da nova sessão
    loadMessages()
  }, [session, loadMessages])
  
  return {
    // Estado das mensagens
    messages,
    isLoadingMessages,
    messagesError,
    hasMoreMessages,
    
    // Funções de mensagens
    loadMessages,
    loadMoreMessages,
    sendMessage,
    uploadImage,
    markAsRead,
    refreshMessages,
    addNewMessage,
    
    // Typing indicator
    isTyping,
    setIsTyping,
    
    // Estado de envio
    isSending,
    isUploading
  }
}

export default useMessages